import {
  Alert,
  Collapse,
  Grid,
  LinearProgress,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";

import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import DeleteIcon from "@mui/icons-material/Delete";
import EditSectionForm from "./EditSectionForm";
import EditSubSectionForm from "./EditSubSectionForm";
import {
  useDeleteSectionMutation,
  useDeleteSubSectionMutation,
  useGetSectionQuery,
  useGetSubSectionQuery,
} from "./sectionsApiSlice";
import AddSectionForm from "./AddSectionForm";
import AddSubsectionForm from "./AddSubsectionForm";
import CustomWarningDialog from "../../../customComponents/CustomWarningDialog";
import toast from "../../../../helpers/toast";

function Sections() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: sectionData, isLoading: sectionLoading } =
    useGetSectionQuery("all");

  const { data: subSectionData, isLoading: subSectionLoading } =
    useGetSubSectionQuery("all");

  const [deleteSection] = useDeleteSectionMutation();
  const [deleteSubSection] = useDeleteSubSectionMutation();

  const [editSection, setEditSection] = React.useState(false);
  const [focusedSection, setFocusedSection] = React.useState(null);
  const [focusedSubSection, setFocusedSubSection] = React.useState(null);
  const [itemToDelete, setItemToDelete] = React.useState(null);
  const [openWarning, setOpenWarning] = React.useState(false);
  const [warningMessage, setWarningMessage] = React.useState(
    "Are you sure you want to delete this item?",
  );
  const [orphanSubsections, setOrphanSubsections] = React.useState([]);
  const [pageOrphans, setPageOrphans] = React.useState([]);
  const [showOrphanAlert, setShowOrphanAlert] = React.useState(false);

  React.useEffect(() => {
    if (!sectionData || !subSectionData) return;
    const sectionIds = new Set(sectionData.map((s) => s.id));
    const orphans = subSectionData.filter(
      (sub) => sub.section_id && !sectionIds.has(sub.section_id),
    );
    if (orphans.length > 0) {
      setPageOrphans(orphans);
      setShowOrphanAlert(true);
    }
  }, [sectionData, subSectionData]);

  const handleDelete = async ({ fn, item }) => {
    try {
      const response = await fn(item).unwrap();
      if (response.message) {
        toast.success(response.message);
      } else {
        toast.success("Error deleting item");
      }
    } catch (error) {
      toast.error("Somethng went wrong");
    }
  };

  return (
    <>
      <Collapse in={showOrphanAlert}>
        <Alert
          severity="warning"
          onClose={() => setShowOrphanAlert(false)}
          sx={{ mb: 2 }}
        >
          <strong>
            {pageOrphans.length} sub-section
            {pageOrphans.length === 1 ? " is" : "s are"} not assigned to any
            section:
          </strong>{" "}
          {pageOrphans.map((s) => s.name).join(", ")}. Please reassign
          {pageOrphans.length === 1 ? " it" : " them"} to avoid missing data.
        </Alert>
      </Collapse>

      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={itemToDelete}
        action={handleDelete}
        message={warningMessage}
        extraContent={
          orphanSubsections.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Affected sub-sections:
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, "& li": { fontSize: "0.875rem" } }}
              >
                {orphanSubsections.map((sub) => (
                  <li key={sub.id}>{sub.name}</li>
                ))}
              </Box>
            </Box>
          ) : null
        }
        blockAction={orphanSubsections.length > 0}
      />

      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={4}>
          <StyledPaper>
            {editSection ? (
              <EditSectionForm
                item={focusedSection}
                setFocusedItem={setFocusedSection}
              />
            ) : (
              <AddSectionForm />
            )}
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={8}>
          <StyledPaper
            onClick={(e) => {
              setEditSection(false);
              setFocusedSection(null);
            }}
          >
            {sectionLoading && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">Sections</Typography>
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {sectionData?.map((section) => (
                  <Box
                    key={section.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      sx={{ color: "primary.main", fontWeight: 600 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFocusedSection(section);
                      }}
                    >
                      {section.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Head title: {section.head_title || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Section head: {section?.head?.profile?.first_name || ""}{" "}
                      {section?.head?.profile?.last_name || ""}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <SvgIcon
                        sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                        component={EditIcon}
                        inheritViewBox
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedSection(section);
                          setEditSection(true);
                        }}
                      />
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          const orphans = section.subsections ?? [];
                          setOrphanSubsections(orphans);
                          setWarningMessage(
                            orphans.length > 0
                              ? `This section has ${orphans.length} sub-section${
                                  orphans.length === 1 ? "" : "s"
                                } assigned to it. Deleting it will leave ${
                                  orphans.length === 1 ? "it" : "them"
                                } without a section. Please reassign before deleting`
                              : "Are you sure you want to delete this section?",
                          );
                          setItemToDelete({ item: section, fn: deleteSection });
                          setOpenWarning(true);
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ pb: 4 }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Section </StyledTableCell>
                      <StyledTableCell align="left">head title</StyledTableCell>
                      <StyledTableCell align="left">
                        Section head
                      </StyledTableCell>
                      <StyledTableCell>edit</StyledTableCell>
                      <StyledTableCell align="right">delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectionData?.map((section) => (
                      <StyledTableRow key={section.id}>
                        <StyledTableCell component="th" scope="section">
                          <Box
                            className="tableCellLink"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedSection(section);
                            }}
                          >
                            {section.name}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {section.head_title}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {section?.head?.profile?.first_name}{" "}
                          {section?.head?.profile?.last_name}
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          <SvgIcon
                            sx={{
                              color: "primary.safeOnLight",
                              cursor: "pointer",
                            }}
                            component={EditIcon}
                            inheritViewBox
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedSection(section);
                              setEditSection(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <DeleteIcon
                            className="tableCellLink"
                            onClick={() => {
                              const orphans = section.subsections ?? [];
                              setOrphanSubsections(orphans);
                              setWarningMessage(
                                orphans.length > 0
                                  ? `This section has ${orphans.length} sub-section${
                                      orphans.length === 1 ? "" : "s"
                                    } assigned to it. Deleting it will leave ${
                                      orphans.length === 1 ? "it" : "them"
                                    } without a section. Please reassign before deleting`
                                  : "Are you sure you want to delete this section?",
                              );
                              setItemToDelete({
                                item: section,
                                fn: deleteSection,
                              });
                              setOpenWarning(true);
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper>
            {focusedSubSection ? (
              <EditSubSectionForm
                item={focusedSubSection}
                setFocusedSubSection={setFocusedSubSection}
              />
            ) : (
              <AddSubsectionForm focusedSection={focusedSection} />
            )}
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={8}>
          <StyledPaper>
            {subSectionLoading && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">Sub-Sections</Typography>
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {subSectionData?.map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography sx={{ color: "primary.main", fontWeight: 600 }}>
                      {row.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Report type: {row.report_type || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Templates: {row.templates.length}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <SvgIcon
                        sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                        component={EditIcon}
                        inheritViewBox
                        onClick={() => {
                          setFocusedSubSection(row);
                        }}
                      />
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          setOrphanSubsections([]);
                          setWarningMessage(
                            "Are you sure you want to delete this sub-section?",
                          );
                          setItemToDelete({ item: row, fn: deleteSubSection });
                          setOpenWarning(true);
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ pb: 4 }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Name </StyledTableCell>
                      <StyledTableCell align="left">
                        report type
                      </StyledTableCell>
                      <StyledTableCell align="left">templates</StyledTableCell>
                      <StyledTableCell align="left">edit</StyledTableCell>
                      <StyledTableCell align="right">delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subSectionData?.map((row) => (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell component="th" scope="row">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row.report_type}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row.templates.length}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <SvgIcon
                            sx={{
                              color: "primary.safeOnLight",
                              cursor: "pointer",
                            }}
                            component={EditIcon}
                            inheritViewBox
                            onClick={() => {
                              setFocusedSubSection(row);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <DeleteIcon
                            className="tableCellLink"
                            onClick={() => {
                              setOrphanSubsections([]);
                              setWarningMessage(
                                "Are you sure you want to delete this sub-section?",
                              );
                              setItemToDelete({
                                item: row,
                                fn: deleteSubSection,
                              });
                              setOpenWarning(true);
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Sections;
